import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
} from "@/components/ui";
import { FileText, Link, Unlink, Search, CheckCircle, XCircle, DollarSign } from "lucide-react";
import { toast } from "react-hot-toast";
import financeService from "@/services/financeService";

const LinkPurchaseRequestsModal = ({ open, onOpenChange, payable, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [availablePRs, setAvailablePRs] = useState([]);
  const [linkedPRs, setLinkedPRs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingPRs, setLoadingPRs] = useState(true);

  useEffect(() => {
    if (open && payable) {
      fetchData();
    }
  }, [open, payable]);

  const fetchData = async () => {
    try {
      setLoadingPRs(true);
      
      // Fetch available purchase requests
      const availableResponse = await financeService.getAvailablePurchaseRequests();
      
      // Fetch current payable details to get linked PRs
      const payableResponse = await financeService.getPayableById(payable.id);
      
      if (availableResponse.data.success) {
        setAvailablePRs(availableResponse.data.data.purchase_requests || []);
      }
      
      if (payableResponse.data.success) {
        const payableData = payableResponse.data.data.payable;
        // If this payable has a linked PR, add it to linkedPRs
        if (payableData.PurchaseRequest) {
          setLinkedPRs([payableData.PurchaseRequest]);
        } else {
          setLinkedPRs([]);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load purchase requests");
    } finally {
      setLoadingPRs(false);
    }
  };

  const handleLinkPR = async (prId) => {
    try {
      setLoading(true);
      await financeService.linkPurchaseRequestToPayable(payable.id, prId);
      toast.success("Purchase request linked successfully");
      await fetchData();
      onSuccess();
    } catch (error) {
      console.error("Error linking purchase request:", error);
      toast.error(error.response?.data?.message || "Failed to link purchase request");
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkPR = async (prId) => {
    try {
      setLoading(true);
      await financeService.unlinkPurchaseRequestFromPayable(payable.id, prId);
      toast.success("Purchase request unlinked successfully");
      await fetchData();
      onSuccess();
    } catch (error) {
      console.error("Error unlinking purchase request:", error);
      toast.error(error.response?.data?.message || "Failed to unlink purchase request");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return financeService.formatCurrency(amount || 0);
  };

  const formatDate = (dateString) => {
    return financeService.formatDate(dateString);
  };

  const filteredPRs = availablePRs.filter((pr) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      pr.request_id?.toLowerCase().includes(searchLower) ||
      pr.vendor_name?.toLowerCase().includes(searchLower) ||
      pr.item_service_requested?.toLowerCase().includes(searchLower)
    );
  });

  if (!payable) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="border-b pb-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Link className="h-6 w-6 text-blue-600" />
              Link Purchase Requests
            </h2>
            <p className="text-gray-600 mt-1">
              Link completed purchase requests to payable: {payable.beneficiary_name}
            </p>
            <div className="mt-2 flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Payable Amount: <span className="font-semibold">{formatCurrency(payable.total_amount)}</span>
              </span>
            </div>
          </div>

          {loadingPRs ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading purchase requests...</span>
            </div>
          ) : (
            <>
              {/* Linked Purchase Requests */}
              {linkedPRs.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Linked Purchase Request
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold">PR ID</TableHead>
                          <TableHead className="font-semibold">Vendor</TableHead>
                          <TableHead className="font-semibold">Item/Service</TableHead>
                          <TableHead className="font-semibold">Amount</TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {linkedPRs.map((pr) => (
                          <TableRow key={pr.id}>
                            <TableCell className="font-medium">{pr.request_id}</TableCell>
                            <TableCell>{pr.vendor_name}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {pr.item_service_requested}
                            </TableCell>
                            <TableCell className="font-semibold">
                              {formatCurrency(pr.amount)}
                            </TableCell>
                            <TableCell>{formatDate(pr.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUnlinkPR(pr.id)}
                                disabled={loading || parseFloat(payable.paid_amount) > 0}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Unlink className="h-4 w-4 mr-1" />
                                Unlink
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {parseFloat(payable.paid_amount) > 0 && (
                    <p className="text-sm text-amber-600 flex items-center gap-1">
                      <XCircle className="h-4 w-4" />
                      Cannot unlink purchase requests after payments have been made
                    </p>
                  )}
                </div>
              )}

              {/* Available Purchase Requests */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Available Purchase Requests
                    <Badge variant="outline" className="ml-2">
                      {filteredPRs.length} available
                    </Badge>
                  </h3>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search PRs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {filteredPRs.length === 0 ? (
                  <div className="text-center py-12 border rounded-lg bg-gray-50">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">
                      {searchTerm ? "No purchase requests match your search" : "No available purchase requests"}
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      {searchTerm ? "Try a different search term" : "All completed PRs are already linked"}
                    </p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-50 z-10">
                        <TableRow>
                          <TableHead className="font-semibold">PR ID</TableHead>
                          <TableHead className="font-semibold">Vendor</TableHead>
                          <TableHead className="font-semibold">Item/Service</TableHead>
                          <TableHead className="font-semibold">Amount</TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPRs.map((pr, index) => (
                          <TableRow
                            key={pr.id}
                            className={index % 2 === 0 ? "bg-white" : "bg-gray-50/50"}
                          >
                            <TableCell className="font-medium">{pr.request_id}</TableCell>
                            <TableCell>{pr.vendor_name}</TableCell>
                            <TableCell className="max-w-xs truncate">
                              {pr.item_service_requested}
                            </TableCell>
                            <TableCell className="font-semibold">
                              {formatCurrency(pr.amount)}
                            </TableCell>
                            <TableCell>{formatDate(pr.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                onClick={() => handleLinkPR(pr.id)}
                                disabled={loading || linkedPRs.length > 0}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                <Link className="h-4 w-4 mr-1" />
                                Link
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkPurchaseRequestsModal;
